'use client'

import { syncUserPreferencesAction } from '@/lib/services/actions/user-preferences'
import {
  clearLocalPreferences,
  hasLocalPreferencesToSync,
  loadLocalPreferences
} from '@/lib/services/storage/user-preferences'
import { showToast } from '@/lib/utils/toast'
import { useCallback, useEffect, useState } from 'react'
import { useCurrentUser } from './use-current-user'

interface PreferenceSyncState {
  isSyncing: boolean
  syncComplete: boolean
  hasLocalPreferences: boolean
  lastSyncTime: number | null
  error: string | null
}

/**
 * Hook to synchronize local preferences with server when a user signs in
 *
 * Handles edge cases like:
 * - Network failures during synchronization
 * - Conflict resolution between local and server preferences
 * - Retry mechanism for failed syncs
 *
 * @returns Object containing synchronization state and methods
 */
export function usePreferenceSync() {
  const user = useCurrentUser()
  const [state, setState] = useState<PreferenceSyncState>({
    isSyncing: false,
    syncComplete: false,
    hasLocalPreferences: false,
    lastSyncTime: null,
    error: null
  })
  const [lastUserId, setLastUserId] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  // Check if there are local preferences to sync
  useEffect(() => {
    const checkLocalPreferences = () => {
      const hasPrefs = hasLocalPreferencesToSync()
      setState((prev) => ({
        ...prev,
        hasLocalPreferences: hasPrefs
      }))
    }

    checkLocalPreferences()
    // Re-check when user changes
    if (user?.id !== lastUserId) {
      checkLocalPreferences()
    }
  }, [user?.id, lastUserId])

  // Synchronize preferences function with retry capability
  const synchronizePreferences = useCallback(
    async (force = false) => {
      // Detect login by checking if userId changed from null to a value
      const isNewLogin = user?.id && user.id !== lastUserId

      // Only sync on new login, when forced, or when retrying
      if ((!isNewLogin && !force && retryCount === 0) || !hasLocalPreferencesToSync() || state.isSyncing) {
        return
      }

      setState((prev) => ({ ...prev, isSyncing: true, error: null }))

      try {
        // Load local preferences to sync to server
        const localPreferences = loadLocalPreferences()

        // Sync preferences to server
        const response = await syncUserPreferencesAction(localPreferences)

        if (response.success) {
          // Clear local preferences now that they're on the server
          clearLocalPreferences()
          setState((prev) => ({
            ...prev,
            isSyncing: false,
            syncComplete: true,
            lastSyncTime: Date.now(),
            error: null,
            hasLocalPreferences: false
          }))

          // Reset retry count on success
          setRetryCount(0)

          // Only show success toast on explicit sync or retry
          if (force || retryCount > 0) {
            showToast({ title: 'Your preferences were successfully synchronized', color: 'success', timeout: 3000 })
          }
        } else {
          // Handle error case
          console.error('Failed to sync preferences:', response.error)
          setState((prev) => ({
            ...prev,
            isSyncing: false,
            error: response.error || 'Failed to sync preferences'
          }))

          // Show error toast
          showToast({
            title: `Failed to sync your preferences: ${response.error || 'Unknown error'}`,
            color: 'danger',
            timeout: 5000
          })

          // Increment retry count
          setRetryCount((prev) => prev + 1)
        }
      } catch (error) {
        console.error('Error synchronizing preferences:', error)
        setState((prev) => ({
          ...prev,
          isSyncing: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }))

        showToast({ title: 'Error synchronizing your preferences', color: 'danger', timeout: 5000 })

        // Increment retry count
        setRetryCount((prev) => prev + 1)
      }
    },
    [user?.id, lastUserId, state.isSyncing, retryCount]
  )

  // Auto-retry logic (max 3 retries with exponential backoff)
  useEffect(() => {
    if (retryCount > 0 && retryCount <= 3 && state.error && !state.isSyncing) {
      const backoffTime = Math.pow(2, retryCount - 1) * 3000 // 3s, 6s, 12s

      const retryTimer = setTimeout(() => {
        console.log(`Retrying preference sync (attempt ${retryCount} of 3)`)
        synchronizePreferences(true)
      }, backoffTime)

      return () => clearTimeout(retryTimer)
    }

    // If we've exceeded max retries, reset counter but keep the error state
    if (retryCount > 3) {
      setRetryCount(0)
      showToast({ title: 'Failed to sync preferences after multiple attempts', color: 'danger', timeout: 5000 })
    }
  }, [retryCount, state.error, state.isSyncing, synchronizePreferences])

  // Effect to trigger synchronization on login
  useEffect(() => {
    const isNewLogin = user?.id && user.id !== lastUserId

    if (isNewLogin) {
      synchronizePreferences()
    }

    // Always update the lastUserId to track authentication changes
    setLastUserId(user?.id || null)
  }, [user?.id, lastUserId, synchronizePreferences])

  // Method to manually trigger synchronization
  const syncPreferences = useCallback(() => {
    if (!user?.id) {
      showToast({ title: 'You must be logged in to sync preferences', color: 'danger', timeout: 5000 })
      return
    }

    synchronizePreferences(true)
  }, [user?.id, synchronizePreferences])

  // Method to clear sync state and errors
  const resetSyncState = useCallback(() => {
    setState((prev) => ({
      ...prev,
      syncComplete: false,
      error: null
    }))
    setRetryCount(0)
  }, [])

  return {
    ...state,
    syncPreferences,
    resetSyncState
  }
}
